import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Mic, MicOff, Play, Pause } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface VoiceAssistantModeProps {
  onBack: () => void;
}

export const VoiceAssistantMode = ({ onBack }: VoiceAssistantModeProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [response, setResponse] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const recordingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recordingStartTimeRef = useRef<number | null>(null);
  const { toast } = useToast();

  const MIN_RECORDING_DURATION = 1000; // 1 second
  const MIN_AUDIO_SIZE = 10000; // 10KB

  useEffect(() => {
    return () => {
      stopAllAudio();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
    };
  }, []);

  const stopAllAudio = () => {
    // Stop ElevenLabs audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    // Stop Web Speech API (fallback)
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    // Stop recording
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsPlaying(false);
    setIsRecording(false);
  };

  const startRecording = async () => {
    try {
      setTranscript("");
      setResponse("");
      setIsRecording(true);
      audioChunksRef.current = [];

      // Request microphone access with optimized settings for speech recognition
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 16000, // 16kHz is standard for speech recognition
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      recordingStartTimeRef.current = Date.now();
      
      console.log('Microphone access granted');
      
      // Setup audio context for visualization
      audioContextRef.current = new AudioContext();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      // Start audio level monitoring
      const updateAudioLevel = () => {
        if (!analyserRef.current) return;
        
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(average);
        
        animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
      };
      updateAudioLevel();

      // Setup MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        console.log('Recording stopped, processing audio...');
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        setAudioLevel(0);
        await processRecording();
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();

      // Auto-stop after 10 seconds
      recordingTimeoutRef.current = setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          stopRecording();
        }
      }, 10000);

      toast({
        title: "Ձայնագրում",
        description: "Խոսեք հայերեն... (առավելագույնը 10 վայրկյան)",
      });
    } catch (error) {
      console.error('Error starting recording:', error);
      setIsRecording(false);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց սկսել ձայնագրումը",
        variant: "destructive",
      });
    }
  };

  const stopRecording = () => {
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const processRecording = async () => {
    try {
      setIsProcessing(true);
      
      // Convert audio chunks to base64
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      
      // Validation: Check recording duration
      const recordingDuration = recordingStartTimeRef.current 
        ? Date.now() - recordingStartTimeRef.current 
        : 0;
      
      console.log('📊 Audio stats:', {
        duration: `${recordingDuration}ms`,
        size: `${audioBlob.size} bytes`,
        type: audioBlob.type
      });
      
      if (recordingDuration < MIN_RECORDING_DURATION) {
        throw new Error(`Ձայնագրումը շատ կարճ է (${recordingDuration}ms)`);
      }
      
      // Validation: Check audio size
      if (audioBlob.size < MIN_AUDIO_SIZE) {
        throw new Error(`Աուդիո ֆայլը շատ փոքր է (${audioBlob.size} bytes)`);
      }
      
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        try {
          const base64Audio = (reader.result as string).split(',')[1];
          
          console.log('📤 Sending audio to Gemini for transcription...');
          console.log('📊 Base64 audio length:', base64Audio.length);
          
          // Send to Gemini for transcription
          const { data: transcriptionData, error: transcriptionError } = await supabase.functions.invoke(
            'gemini-speech-to-text',
            {
              body: { audio: base64Audio }
            }
          );

          if (transcriptionError) {
            throw transcriptionError;
          }

          const transcriptText = transcriptionData.text;
          console.log('Transcription received:', transcriptText);
          setTranscript(transcriptText);

          if (transcriptText && transcriptText.trim()) {
            // Process with career advisor
            await processWithCareerAdvisor(transcriptText);
          } else {
            toast({
              title: "Սխալ",
              description: "Չհաջողվեց ճանաչել խոսքը",
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error('Error in reader.onloadend:', error);
          toast({
            title: "Սխալ",
            description: error instanceof Error ? error.message : "Չհաջողվեց մշակել ձայնագրումը",
            variant: "destructive",
          });
        } finally {
          setIsProcessing(false);
        }
      };
      
      reader.onerror = () => {
        console.error('FileReader error');
        toast({
          title: "Սխալ",
          description: "Չհաջողվեց կարդալ աուդիո ֆայլը",
          variant: "destructive",
        });
        setIsProcessing(false);
      };
      
      reader.readAsDataURL(audioBlob);
      
    } catch (error) {
      console.error('Error processing recording:', error);
      setIsProcessing(false);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց մշակել ձայնագրումը",
        variant: "destructive",
      });
    }
  };

  const processWithCareerAdvisor = async (text: string) => {
    console.log('Sending to career advisor:', text);
    setIsProcessing(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('career-advisor', {
        body: { 
          message: text,
          stream: false // Use non-streaming for voice mode
        }
      });

      if (error) {
        console.error('Career advisor error:', error);
        throw new Error('Չհաջողվեց ստանալ պատասխան խորհրդատուից');
      }

      console.log('Career advisor response:', data);
      
      const responseText = data?.response || data?.message || "";
      
      if (!responseText || !responseText.trim()) {
        throw new Error('Պատասխանը դատարկ է');
      }
      
      setResponse(responseText);
      
      // Play the response as audio
      try {
        await playTextAsAudio(responseText);
      } catch (audioError) {
        console.error('Error playing audio:', audioError);
        // Don't throw - audio playback is not critical
        toast({
          title: "Ուշադրություն",
          description: "Պատասխանը ստացված է, բայց չհաջողվեց նվագարկել",
        });
      }
    } catch (error) {
      console.error('Career advisor processing error:', error);
      toast({
        title: "Սխալ",
        description: error instanceof Error ? error.message : "Չհաջողվեց մշակել հարցումը",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const playTextAsAudio = async (text: string) => {
    try {
      console.log('🎵 Starting ElevenLabs TTS for:', text.substring(0, 50) + '...');
      setIsPlaying(true);

      // Call ElevenLabs TTS edge function
      const { data, error } = await supabase.functions.invoke('text-to-speech', {
        body: { 
          text,
          voiceId: '9BWtsMINqrJLrRacOk9x' // Aria voice - excellent for multilingual
        }
      });

      if (error) {
        console.error('❌ ElevenLabs TTS error:', error);
        toast({
          title: "Սխալ",
          description: "Չհաջողվեց ստեղծել աուդիո պատասխան",
          variant: "destructive",
        });
        setIsPlaying(false);
        return;
      }

      if (!data?.audioContent) {
        console.error('❌ No audio content received');
        toast({
          title: "Սխալ",
          description: "Աուդիո պատասխան չստացվեց",
          variant: "destructive",
        });
        setIsPlaying(false);
        return;
      }

      console.log('✅ Audio received from ElevenLabs');

      // Convert base64 to blob and play
      const audioBlob = await fetch(`data:audio/mp3;base64,${data.audioContent}`).then(r => r.blob());
      const audioUrl = URL.createObjectURL(audioBlob);

      // Create and play audio
      if (audioRef.current) {
        audioRef.current.pause();
      }

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        console.log('✅ Audio playback completed');
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = (e) => {
        console.error('❌ Audio playback error:', e);
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
        toast({
          title: "Սխալ",
          description: "Աուդիո նվագարկման սխալ",
          variant: "destructive",
        });
      };

      await audio.play();
      console.log('🔊 Audio playing...');

    } catch (error) {
      console.error('❌ Failed to play audio:', error);
      setIsPlaying(false);
      toast({
        title: "Սխալ",
        description: "Աուդիո նվագարկման սխալ",
        variant: "destructive",
      });
    }
  };

  const stopPlayback = () => {
    stopAllAudio();
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Ձայնային խորհրդատու</h3>
        <p className="text-muted-foreground">
          Սեղմեք միկրոֆոնը և խոսեք ձեր հարցի մասին
        </p>
      </div>

      <Card className="p-6">
        <CardContent className="space-y-6 p-0">
          {/* Voice Recording Controls */}
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              <Button
                size="lg"
                variant={isRecording ? "destructive" : "default"}
                className="w-20 h-20 rounded-full transition-all"
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isProcessing}
              >
                {isRecording ? (
                  <MicOff className="h-8 w-8" />
                ) : (
                  <Mic className="h-8 w-8" />
                )}
              </Button>
              
              {/* Audio Level Indicator */}
              {isRecording && (
                <div 
                  className="absolute inset-0 rounded-full border-4 border-primary animate-ping"
                  style={{ opacity: Math.min(audioLevel / 128 + 0.2, 0.7) }}
                />
              )}
            </div>

            {/* Audio Level Bar */}
            {isRecording && (
              <div className="w-48 h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-100"
                  style={{ width: `${Math.min((audioLevel / 128) * 100, 100)}%` }}
                />
              </div>
            )}

            <p className="text-sm text-muted-foreground text-center font-medium">
              {isRecording ? "🎤 Ձայնագրում է... (առավելագույնը 10 վայրկյան)" : 
               isProcessing ? "⚙️ Մշակվում է..." : 
               "Սեղմեք միկրոֆոնը և խոսեք"}
            </p>
          </div>

          {/* Transcript */}
          {transcript && (
            <div className="bg-muted p-4 rounded-lg">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <span>Ձեր հարցը:</span>
              </h4>
              <p className="text-sm">{transcript}</p>
            </div>
          )}

          {/* Response */}
          {response && (
            <div className="bg-primary/10 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium">Պատասխան:</h4>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={isPlaying ? stopPlayback : () => playTextAsAudio(response)}
                >
                  {isPlaying ? (
                    <>
                      <Pause className="h-4 w-4 mr-1" />
                      Դադարեցնել
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-1" />
                      Նվագել
                    </>
                  )}
                </Button>
              </div>
              <p className="text-sm whitespace-pre-wrap">{response}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Button 
        variant="ghost" 
        onClick={() => {
          stopAllAudio();
          onBack();
        }} 
        className="w-full"
      >
        Հետ դառնալ
      </Button>
    </div>
  );
};
